import { FormEvent, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { apiRequest, formatMoney, getToken } from "../api";
import { sitePath, siteResolveQuery, useSiteSlug } from "../site-context";

type Service = {
  id: string;
  name: string;
  description?: string | null;
  pricingType: string;
  priceCents?: number | null;
};

export function SiteBookPage() {
  const { slug: paramSlug = "" } = useParams();
  const slug = useSiteSlug(paramSlug);
  const navigate = useNavigate();
  const [businessId, setBusinessId] = useState<string | null>(null);
  const [businessName, setBusinessName] = useState("");
  const [currency, setCurrency] = useState("USD");
  const [services, setServices] = useState<Service[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const [form, setForm] = useState({
    serviceId: "",
    preferredStartsAt: "",
    addressLine1: "",
    addressLine2: "",
    city: "",
    region: "",
    postalCode: "",
    country: "US",
    clientNotes: "",
  });

  useEffect(() => {
    const qs = siteResolveQuery(slug, true);
    if (!qs.includes("slug=") && !qs.includes("host=")) return;
    void apiRequest<{
      site: {
        business: { id: string; name: string; currencyCode?: string | null };
        services: Service[];
      };
    }>(`/sites/resolve?${qs}`)
      .then((data) => {
        setBusinessId(data.site.business.id);
        setBusinessName(data.site.business.name);
        setCurrency(data.site.business.currencyCode ?? "USD");
        setServices(data.site.services);
        setForm((prev) => ({
          ...prev,
          serviceId: data.site.services[0]?.id ?? "",
        }));
      })
      .catch((err: Error) => setError(err.message));
  }, [slug]);

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    if (!businessId) return;
    if (!getToken()) {
      navigate(sitePath(slug, "login"));
      return;
    }
    setPending(true);
    setError(null);
    try {
      const preferredStartsAt = new Date(form.preferredStartsAt).toISOString();
      await apiRequest(`/businesses/${businessId}/portal/bookings`, {
        method: "POST",
        body: JSON.stringify({
          serviceId: form.serviceId,
          preferredStartsAt,
          addressLine1: form.addressLine1,
          addressLine2: form.addressLine2 || undefined,
          city: form.city,
          region: form.region,
          postalCode: form.postalCode,
          country: form.country,
          clientNotes: form.clientNotes || undefined,
        }),
      });
      navigate(sitePath(slug, "portal"));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Booking failed");
    } finally {
      setPending(false);
    }
  }

  return (
    <div className="auth-layout">
      <form
        className="auth-card"
        onSubmit={onSubmit}
        style={{ width: "min(560px, 100%)" }}
      >
        <p className="eyebrow">Book a Service</p>
        <h1>Book with {businessName || "…"}</h1>
        {!getToken() ? (
          <p className="notice">
            <Link to={sitePath(slug, "login")}>
              Log in to {businessName || "this business"}
            </Link>{" "}
            or <Link to={sitePath(slug, "register")}>create an account</Link> to
            submit a booking.
          </p>
        ) : null}
        <div className="form-grid">
          <label>
            Service
            <select
              required
              value={form.serviceId}
              onChange={(e) => setForm({ ...form, serviceId: e.target.value })}
            >
              {services.map((service) => (
                <option key={service.id} value={service.id}>
                  {service.name} — {formatMoney(service.priceCents, currency)}
                </option>
              ))}
            </select>
          </label>
          <label>
            Preferred date & time
            <input
              required
              type="datetime-local"
              value={form.preferredStartsAt}
              onChange={(e) =>
                setForm({ ...form, preferredStartsAt: e.target.value })
              }
            />
          </label>
          <label>
            Address
            <input
              required
              value={form.addressLine1}
              onChange={(e) =>
                setForm({ ...form, addressLine1: e.target.value })
              }
            />
          </label>
          <div className="form-grid two">
            <label>
              City
              <input
                required
                value={form.city}
                onChange={(e) => setForm({ ...form, city: e.target.value })}
              />
            </label>
            <label>
              Region
              <input
                required
                value={form.region}
                onChange={(e) => setForm({ ...form, region: e.target.value })}
              />
            </label>
          </div>
          <div className="form-grid two">
            <label>
              Postal code
              <input
                required
                value={form.postalCode}
                onChange={(e) =>
                  setForm({ ...form, postalCode: e.target.value })
                }
              />
            </label>
            <label>
              Country
              <input
                required
                maxLength={2}
                value={form.country}
                onChange={(e) =>
                  setForm({ ...form, country: e.target.value.toUpperCase() })
                }
              />
            </label>
          </div>
          <label>
            Notes
            <textarea
              value={form.clientNotes}
              onChange={(e) =>
                setForm({ ...form, clientNotes: e.target.value })
              }
            />
          </label>
          <button
            className="btn btn-primary"
            type="submit"
            disabled={pending || !form.serviceId}
          >
            {pending ? "Submitting..." : "Request booking"}
          </button>
          {error ? <p className="notice error">{error}</p> : null}
          <p className="notice">
            <Link to={sitePath(slug)}>Back to website</Link>
          </p>
        </div>
      </form>
    </div>
  );
}
